import { AntInput } from "@/CommonElements/AntInput";
import { AntSelect } from "@/CommonElements/AntSelect";
import { Controllers } from "@/CommonElements/Controller";
import { PageHeader } from "@/CommonElements/PageTitle";
import TableC from "@/CommonElements/TableC";
import { ActionsFooter } from "@/Component/ActionsFooter";
import { cn, filterObject } from "@/utils/helpers";
import { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Container, Spinner } from "reactstrap";
import {
  useHouseContractCampaignsReport,
  useHouseContractCampaignsReportMutation,
} from "@/services/reports";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";

export const HouseContractCampaignsReport = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const { getLocalizedText } = useLocalizedText();

  const {
    mutate: generateHouseContractCampaignsReport,
    isPending: isGeneratingHouseContractCampaignsReport,
  } = useHouseContractCampaignsReportMutation();

  const form = useForm({
    defaultValues: {
      format: "pdf",
      orientation: "portrait",
    },
    disabled: isGeneratingHouseContractCampaignsReport,
  });

  const format = form.watch("format");
  const isJson = format === "json";

  const filters = filterObject({
    ...form.getValues(),
    format: "json",
    page,
  });

  const {
    data: houseContractCampaignsRes,
    isLoading: isLoadingHouseContractCampaigns,
    refetch,
    isRefetching: isRefetchingHouseContractCampaigns,
  } = useHouseContractCampaignsReport({
    params: filters,
  });

  const rows = houseContractCampaignsRes?.data?.data || [];
  const meta = houseContractCampaignsRes?.data?.meta || {};

  const {
    control,
    formState: { errors },
    getValues,
  } = form;

  const formText = {
    json: {
      buttonText: t("VIEW_REPORTS"),
    },
    pdf: {
      buttonText: t("EXPORT_PDF"),
    },
    excel: {
      buttonText: t("EXPORT_EXCEL"),
    },
  };

  const formContent = formText[format] || formText.json;

  useEffect(() => {
    if (isJson && page > 1) {
      refetch();
    }
  }, [page, isJson, refetch]);

  const ctrls = [
    {
      labelProps: { children: t("REPORT_FORMAT") },
      controllerProps: {
        name: "format",
        render: ({ field, fieldState: { error } }) => (
          <AntSelect
            {...field}
            allowClear
            id="format"
            placeholder={t("REPORT_FORMAT")}
            status={error ? "error" : undefined}
            options={[
              { value: "json", label: t("VIEW_REPORTS") },
              { value: "pdf", label: t("EXPORT_PDF") },
              { value: "excel", label: t("EXPORT_EXCEL") },
            ]}
          />
        ),
      },
    },
    !isJson && {
      labelProps: { children: t("ORIENTATION") },
      controllerProps: {
        name: "orientation",
        render: ({ field, fieldState: { error } }) => (
          <AntSelect
            {...field}
            allowClear
            id="orientation"
            placeholder={t("ORIENTATION")}
            status={error ? "error" : undefined}
            options={[
              { value: "portrait", label: t("PORTRAIT") },
              { value: "landscape", label: t("LANDSCAPE") },
            ]}
          />
        ),
      },
    },
    {
      labelProps: { children: t("BUILDING_NAME") },
      controllerProps: {
        name: "house_name",
        render: ({ field, fieldState: { error } }) => (
          <AntInput
            {...field}
            allowClear
            id="house_name"
            placeholder={t("BUILDING_NAME_PLACEHOLDER")}
            status={error ? "error" : undefined}
          />
        ),
      },
    },
    {
      labelProps: { children: t("BUILDING_NO") },
      controllerProps: {
        name: "building_no",
        render: ({ field, fieldState: { error } }) => (
          <AntInput
            {...field}
            allowClear
            id="building_no"
            placeholder={t("BUILDING_NO_PLACEHOLDER")}
            status={error ? "error" : undefined}
          />
        ),
      },
    },
    {
      labelProps: { children: t("BUILDING_CODE") },
      controllerProps: {
        name: "building_code",
        render: ({ field, fieldState: { error } }) => (
          <AntInput
            {...field}
            allowClear
            id="building_code"
            placeholder={t("BUILDING_CODE_PLACEHOLDER")}
            status={error ? "error" : undefined}
          />
        ),
      },
    },
    {
      labelProps: { children: t("SERVICE_CENTER") },
      controllerProps: {
        name: "service_center_id",
        render: ({ field, fieldState: { error } }) => (
          <SelectCachedOptions
            {...field}
            allowClear
            id={field.name}
            placeholder={t("SERVICE_CENTER")}
            status={error ? "error" : undefined}
            optionKey="service_centers"
            options={(sc) => ({
              value: sc.id,
              label: getLocalizedText(sc.name_ar, sc.name_en),
            })}
          />
        ),
      },
    },
    {
      labelProps: { children: t("PERMIT_NO") },
      controllerProps: {
        name: "contract_id",
        render: ({ field, fieldState: { error } }) => (
          <AntInput
            {...field}
            allowClear
            id="contract_id"
            placeholder={t("PERMIT_NO_PLACEHOLDER")}
            status={error ? "error" : undefined}
          />
        ),
      },
    },
  ].filter(Boolean);

  const columns = [
    {
      name: t("SERIAL"),
      key: "serial",
    },
    {
      name: t("BUILDING_NO"),
      key: "building_no",
    },
    {
      name: t("BUILDING_CODE"),
      key: "building_code",
    },
    {
      name: t("ORGANIZER"),
      key: "organizer_name",
    },
    {
      name: t("CONTRACT_NUMBER"),
      key: "contract_id",
    },
    {
      name: t("HOUSE_CONTRACT_CAMPAIGNS_COL_COUNT"),
      key: "total_count",
    },
    {
      name: t("BUILDING_NAME"),
      key: "house_name_ar",
      render: (_, row) =>
        getLocalizedText(row?.house_name_ar, row?.house_name_en),
    },
    {
      name: t("ADDRESS"),
      key: "address",
    },
    {
      name: t("SERVICE_CENTER_NAME"),
      key: "service_center_name",
    },
  ];

  const onSubmit = (data) => {
    const payload = {
      ...getValues(),
      page,
    };

    if (data.format === "json") {
      setPage(1);
      return refetch();
    }

    generateHouseContractCampaignsReport({ params: payload });
  };

  const backTo =
    window.history.length > 1 ? -1 : `/app/madina/housing-reports`;

  const isLoading =
    isGeneratingHouseContractCampaignsReport ||
    isLoadingHouseContractCampaigns;

  return (
    <div>
      <PageHeader
        title={t("HOUSE_CONTRACT_CAMPAIGNS_REPORT_TITLE")}
        sticky={false}
      />
      <Container fluid>
        <form onSubmit={form.handleSubmit(onSubmit)} className="mb-4">
          <Controllers
            watch={form.watch}
            controllers={ctrls.map((config) => ({
              ...config,
              labelProps: {
                ...config.labelProps,
                className: cn("fw-bold", config.labelProps.className),
              },
              controllerProps: {
                ...config.controllerProps,
                control,
                errors,
              },
              layout: {
                colProps: { xs: 6, md: 6 },
              },
            }))}
          />
          <ActionsFooter>
            <Button
              disabled={isLoading}
              className="btn btn-primary btn-sm"
              type="submit"
            >
              {isLoading ? <Spinner size="sm" /> : formContent.buttonText}
            </Button>
            <Link
              to={backTo}
              disabled={isLoading}
              style={{ pointerEvents: isLoading ? "none" : "auto" }}
              className="btn btn-danger btn-sm"
              type="button"
            >
              {t("CANCEL")}
            </Link>
            <button
              onClick={() => form.reset()}
              disabled={isLoading}
              className="btn btn-outline-primary btn-sm"
              type="button"
            >
              {t("RESET")}
            </button>
          </ActionsFooter>
        </form>

        {isJson && (
          <TableC
            columnsFilter={false}
            data={rows}
            columns={columns}
            loading={
              isLoadingHouseContractCampaigns ||
              isRefetchingHouseContractCampaigns
            }
            skeletonCount={10}
            pagination={{
              current: page,
              total: meta?.total || 0,
              pageSize: meta?.per_page || 10,
              onChange: (newPage) => {
                setPage(newPage);
              },
            }}
          />
        )}
      </Container>
    </div>
  );
};
